import { useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import {
  MotionBox,
  viewAppearDefault,
  staggeredAppear,
} from "utils/motion";
import Text from "components/base/Text";

function ImageSequenceSection({
  title,
  noPin,
  description,
  url,
  numFrames,
  width,
  height,
  animationId,
  fullScreen,
  children,
  bg,
  extension = "jpg",
  ...props
}) {
  useEffect(() => {
    const isiOS = /^iP/.test(navigator.platform);
    gsap.registerPlugin(ScrollTrigger);

    ScrollTrigger.defaults({
      toggleActions: "restart pause resume pause",
      markers: false,
    });

    if (isiOS) {
      ScrollTrigger.config({
        autoRefreshEvents: "visibilitychange,DOMContentLoaded,load",
      });
    }

    ScrollTrigger.matchMedia({
      // all
      all() {
        const canvas = document.getElementById(`canvas-${animationId}`);
        const context = canvas.getContext("2d");

        canvas.width = width;
        canvas.height = height;

        const frameCount = numFrames;
        const currentFrame = (index) =>
          `${url}${(index + 1).toString().padStart(3, "0")}.${extension}`;
        const images = [];
        const sequence = {
          frame: 0,
        };

        for (let i = 0; i < frameCount; i++) {
          const img = new Image();
          img.src = currentFrame(i);
          images.push(img);
        }

        function render() {
          try {
            context.clearRect(0, 0, canvas.width, canvas.height);
            context.drawImage(images[sequence.frame], 0, 0);
            // eslint-disable-next-line no-empty, no-unused-vars
          } catch (e) {}
        }

        gsap.to(sequence, {
          frame: frameCount - 1,
          snap: "frame",
          scrollTrigger: {
            id: `SEQUENCE-${animationId}`,
            trigger: `#header-sequence-${animationId}`,
            scrub: 1,
            start: "center 50%",
            end: "bottom 50%",
            pin: !noPin,
          },
          onUpdate: render,
        });

        images[0].onload = render;
      },
    });
  }, []);

  return (
    <>
      <MotionBox bg={bg || "black.900"} {...props}>
        <MotionBox
          id={`header-sequence-${animationId}`}
          align="center"
          position="relative"
        >
          <MotionBox
            id={`bg-${animationId}`}
            maxW={fullScreen ? null : "xl"}
            bg={bg || "black.900"}
          >
            {/* canvas style is on global.js file */}
            <canvas id={`canvas-${animationId}`} />
          </MotionBox>

          <MotionBox position="absolute" w="100%" align="center" h="100vh" top={0}>
            <MotionBox maxW="xl" w="100%" h="100%" position="relative">
              {title && description && (
                <MotionBox
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.7 }}
                  variants={staggeredAppear}
                  align="left"
                  position="absolute"
                  bottom="0"
                  opacity={1}
                  right={["inherit", null, 0]}
                  left={[8, null, "inherit"]}
                  pb="xl"
                  mr="xxl"
                >
                  <Text
                    type="headline_sm"
                    color="white"
                    mb="md"
                    variants={viewAppearDefault}
                  >
                    {title}
                  </Text>
                  <Text
                    type="p_md"
                    color="white"
                    maxW="400px"
                    variants={viewAppearDefault}
                  >
                    {description}
                  </Text>
                </MotionBox>
              )}
              {children}
            </MotionBox>
          </MotionBox>
        </MotionBox>
      </MotionBox>
    </>
  );
}

export default ImageSequenceSection;
